/**
 * The invoice dashboard's two reads, and what each one depends on (R5).
 *
 * TWO REQUESTS, DELIBERATELY NOT ONE. The table shows a page of a filtered
 * population; the four cards above it describe that population BEFORE the
 * table's own status filter. That difference is the whole reason the cards are
 * usable as navigation — an operator who has narrowed the list to "Bezahlt"
 * still needs to see how much is open. So a status change, a page turn and a
 * sort reload the LIST only, while the search term, the month and the source
 * reload BOTH, because those three genuinely change which invoices are being
 * described. The matrix is mechanical and is pinned test by test.
 *
 * NOTHING HERE COMPUTES A FIGURE. The summary arrives whole from R4 and is
 * handed on untouched; the rows arrive from R3 in the order and the quantity the
 * server chose. A total folded out of `items` would be a total over ONE PAGE,
 * would change when the reader turned to page two, and would disagree with the
 * card above it — which is the defect this programme exists to remove.
 *
 * STALE RESPONSES are the ordinary case, not an edge one: type "RE-0" then
 * "RE-00" and the first answer can land after the second. Each read keeps its own
 * monotonic generation counter and only the newest is allowed to write state —
 * the same mechanic `useBexioList` uses, kept local because the summary's
 * envelope has neither `items` nor `total` and could not travel through it.
 *
 * THE OFFSET IS RESET DURING RENDER, not from an effect. An effect would let one
 * request go out with the new filter and the old page number before the reset
 * landed, so every filter change would cost two requests and the first would be
 * discarded. Adjusting the state while rendering is React's own answer to this,
 * and it means the loaders below see a consistent pair on their first commit.
 *
 * NO SOURCE, NO REQUEST. `source` is resolved by R2 and can legitimately be null
 * — while the capability read is in flight, or after it failed. Guessing one
 * here would show an organisation a world it may not have.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  fetchInvoiceDashboardInvoices,
  fetchInvoiceDashboardSummary,
} from "../../../api/adminData/invoiceDashboard";
import { useDebouncedValue } from "../../../components/admin/bexio/workspace/useDebouncedValue";
import { monthEndDateString } from "../uebersicht/uebersichtHelpers";
import { currentBusinessPeriod } from "../../../utils/datetime";

/** The page size the reader gets before choosing one. */
export const DEFAULT_LIMIT = 25;

/** Newest invoice first — the only ordering that needs no explanation. */
export const DEFAULT_SORT = "issue_date";
export const DEFAULT_DIRECTION = "desc";

/** The four columns R3 will order by. Nothing else is offered as a header. */
export const SORTABLE_FIELDS = ["invoice_number", "amount", "issue_date", "due_date"];

const pad2 = (n) => String(n).padStart(2, "0");

/**
 * A period from the shared picker → the two dates R3 and R4 accept.
 *
 * An unbounded period (the picker's "Gesamte Zeit") carries a null `from`, and
 * that is exactly the case where NEITHER date is sent: bounding it at the
 * current month would silently hide every invoice raised earlier, which is most
 * of the overdue ones.
 */
export function periodToRange(period) {
  if (!period || period.from == null || period.to == null) {
    return { fromDate: undefined, toDate: undefined };
  }
  return {
    fromDate: `${period.from.year}-${pad2(period.from.month)}-01`,
    toDate: monthEndDateString(period.to.year, period.to.month),
  };
}

/**
 * The period the dashboard opens on: unbounded.
 *
 * "Überfällig" is a statement about invoices raised in earlier months, so a
 * dashboard that defaulted to the current one would answer the question it most
 * needs to answer with a near-permanent zero. The reader narrows deliberately.
 *
 * The `to` anchor is the SWISS business month (Regel 51), not the device's — it
 * is what the picker offers as the upper bound when a reader switches to a
 * bounded preset.
 */
export function defaultInvoicePeriod() {
  // `mode: "all"` is the shared picker's own word for unbounded, and using it is
  // what makes the trigger read "Gesamte Zeit" instead of naming a month it is
  // not filtering by. It is a PERIOD vocabulary and has nothing to do with the
  // source — there is no "all" source, here or anywhere (guard S1).
  const { year, month } = currentBusinessPeriod();
  return { mode: "all", from: null, to: { year, month } };
}

/**
 * One read, with its own generation guard and its own error.
 *
 * `enabled` false means the question cannot be asked yet; the hook then holds
 * nothing and reports neither loading nor failure, because "we have not asked"
 * is a third state and it is not an error.
 *
 * EXPORTED FOR THE PAYMENT LIST (R6b), which needs exactly this stale guard and
 * must not carry a second copy of it. A duplicated generation counter is two
 * places for the same bug to be fixed in one of them.
 */
export function useGuardedRead({ fetcher, params, enabled, failureMessage }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!!enabled);
  const [error, setError] = useState("");

  const generation = useRef(0);
  // `params` is a fresh object every render; serialising it keeps the effect
  // from re-firing when nothing actually changed.
  const paramKey = JSON.stringify(params);

  const load = useCallback(async () => {
    if (!enabled) return;
    const mine = ++generation.current;
    setLoading(true);
    setError("");
    try {
      const res = await fetcher(JSON.parse(paramKey));
      if (mine !== generation.current) return; // superseded
      setData(res);
    } catch (e) {
      if (mine !== generation.current) return;
      // The previous payload is dropped rather than left on screen: figures from
      // an unknown moment presented as current ones are worse than none.
      setData(null);
      setError(e?.message || failureMessage);
    } finally {
      if (mine === generation.current) setLoading(false);
    }
  }, [fetcher, paramKey, enabled, failureMessage]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, reload: load };
}

/**
 * Everything the dashboard needs to ask its two questions.
 *
 * `source` comes from R2 and is never chosen here. `tenantId` is accepted so a
 * future tenant-scoped view can reuse this hook unchanged; R5 renders no control
 * for it.
 */
export default function useInvoiceDashboardData({
  source,
  tenantId,
  // R6b: the invoice workspace is one of two on this page, and the hook is
  // still CALLED while the other one is on screen — Rules of Hooks leave no
  // choice. `active` is how "not on screen" is expressed, and it defaults to
  // true so every caller written before there was a second workspace keeps its
  // behaviour exactly.
  active = true,
} = {}) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [period, setPeriod] = useState(defaultInvoicePeriod);
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [direction, setDirection] = useState(DEFAULT_DIRECTION);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [offset, setOffset] = useState(0);

  const debouncedQ = useDebouncedValue(q);
  const { fromDate, toDate } = useMemo(() => periodToRange(period), [period]);

  // Everything that changes WHICH invoices are described, as opposed to which
  // page of them is shown. A change to any of these puts the reader back on the
  // first page — page seven of a different result set is not a place.
  const populationKey = JSON.stringify({
    source,
    debouncedQ,
    status,
    fromDate,
    toDate,
    tenantId,
    sort,
    direction,
  });
  const [seenPopulation, setSeenPopulation] = useState(populationKey);
  if (seenPopulation !== populationKey) {
    setSeenPopulation(populationKey);
    setOffset(0);
  }
  // Within this render the reset has not been applied yet, so the loaders are
  // told the page number that will actually be in force.
  const effectiveOffset = seenPopulation === populationKey ? offset : 0;

  const enabled = active && (source === "bexio" || source === "native");

  /** What both reads share: the population, never the page. */
  const shared = useMemo(
    () => ({
      source,
      q: debouncedQ || undefined,
      fromDate,
      toDate,
      tenantId: tenantId || undefined,
    }),
    [source, debouncedQ, fromDate, toDate, tenantId]
  );

  const summary = useGuardedRead({
    fetcher: fetchInvoiceDashboardSummary,
    params: shared,
    enabled,
    failureMessage: "Kennzahlen konnten nicht geladen werden.",
  });

  /** The population, plus the page and the ordering of it. */
  const listParams = useMemo(
    () => ({
      ...shared,
      // "Alle" is the absence of a filter, not a word: R3 answers a literal
      // status of "all" with a 422, and rightly so.
      status: status || undefined,
      sort,
      direction,
      limit,
      offset: effectiveOffset,
    }),
    [shared, status, sort, direction, limit, effectiveOffset]
  );

  const list = useGuardedRead({
    fetcher: fetchInvoiceDashboardInvoices,
    params: listParams,
    enabled,
    failureMessage: "Rechnungen konnten nicht geladen werden.",
  });

  const toggleSort = useCallback(
    (field) => {
      if (!SORTABLE_FIELDS.includes(field)) return;
      // A new column starts at its most useful end — largest amount, newest
      // date — and only a second click on the SAME column reverses it.
      if (sort === field) {
        setDirection((d) => (d === "desc" ? "asc" : "desc"));
        return;
      }
      setSort(field);
      setDirection(DEFAULT_DIRECTION);
    },
    [sort]
  );

  const reset = useCallback(() => {
    setQ("");
    setStatus("");
    setPeriod(defaultInvoicePeriod());
  }, []);

  const isFiltered = Boolean(debouncedQ || status || fromDate);

  return {
    q,
    debouncedQ,
    setQ,
    status,
    setStatus,
    period,
    setPeriod,
    fromDate,
    toDate,
    sort,
    direction,
    toggleSort,
    limit,
    setLimit,
    offset: effectiveOffset,
    setOffset,
    isFiltered,
    reset,
    summary: {
      data: summary.data,
      loading: summary.loading,
      error: summary.error,
      reload: summary.reload,
    },
    list: {
      items: Array.isArray(list.data?.items) ? list.data.items : [],
      total: typeof list.data?.total === "number" ? list.data.total : 0,
      asOfDate: list.data?.as_of_date ?? null,
      loading: list.loading,
      error: list.error,
      reload: list.reload,
    },
  };
}
